/**
 * Calendar Service
 *
 * Provides functionality to add events to mobile device calendars from a PWA.
 * Uses Web Share API when available (mobile), falls back to downloading .ics file.
 */

import { CalendarEvent } from '../types';

/**
 * Formats a date to iCalendar format (UTC)
 *
 * The Date object stores UTC internally. When the backend sends UTC dates,
 * we parse them and the Date object represents that exact moment in time.
 * For ICS files, we use UTC format (with Z suffix) so calendar apps can
 * correctly convert to the user's local timezone.
 *
 * For all-day events: YYYYMMDD (no timezone, represents a date)
 * For timed events: YYYYMMDDTHHmmssZ (UTC time)
 */
function formatDate(date: Date, allDay: boolean = false): string {
  // Use UTC values for ICS format - calendar apps will convert to local timezone
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  if (allDay) {
    return `${year}${month}${day}`;
  }

  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  // Use UTC time with Z suffix - calendar apps will convert to user's timezone
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Escapes special characters in iCalendar text fields
 */
function escapeText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Generates an iCalendar (.ics) file content from a calendar event
 *
 * The event dates are in local timezone (converted from UTC by JavaScript Date).
 * The ICS file uses local time values so calendar apps interpret them correctly
 * in the user's timezone.
 */
export function generateICS(event: CalendarEvent): string {
  const lines: string[] = [];

  // iCalendar header
  lines.push('BEGIN:VCALENDAR');
  lines.push('VERSION:2.0');
  lines.push('PRODID:-//Enpal For U//Calendar Service//EN');
  lines.push('CALSCALE:GREGORIAN');
  lines.push('METHOD:PUBLISH');

  // Event
  lines.push('BEGIN:VEVENT');
  lines.push(`UID:${Date.now()}-${Math.random().toString(36).substring(7)}@enpal-for-u`);
  // DTSTAMP is the creation timestamp in UTC
  lines.push(`DTSTAMP:${formatDate(new Date())}`);
  // DTSTART and DTEND are in UTC (from backend) - calendar apps will convert to user's timezone
  lines.push(`DTSTART${event.allDay ? ';VALUE=DATE' : ''}:${formatDate(event.startDate, event.allDay)}`);
  lines.push(`DTEND${event.allDay ? ';VALUE=DATE' : ''}:${formatDate(event.endDate, event.allDay)}`);
  lines.push(
    `SUMMARY:${escapeText(`ENPAL: il tuo appuntamento - ${event.startDate.toLocaleString('it-IT', { timeZone: 'Europe/Rome' })}`)}`
  );

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeText(event.description)}`);
  }

  if (event.location) {
    lines.push(`LOCATION:${escapeText(event.location)}`);
  }

  if (event.organizer) {
    lines.push(`ORGANIZER;CN=${escapeText(event.organizer.name)}:MAILTO:${event.organizer.email}`);
  }

  if (event.attendees && event.attendees.length > 0) {
    event.attendees.forEach(attendee => {
      lines.push(`ATTENDEE;CN=${escapeText(attendee.name)}:MAILTO:${attendee.email}`);
    });
  }

  if (event.url) {
    lines.push(`URL:${event.url}`);
  }

  lines.push('STATUS:CONFIRMED');
  lines.push('SEQUENCE:0');
  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');

  return lines.join('\r\n');
}

/**
 * Creates a Blob from ICS content with proper MIME type
 */
function createICSBlob(icsContent: string): Blob {
  // Use the most compatible MIME type for calendar files
  // Try multiple MIME types that iOS might recognize
  return new Blob([icsContent], {
    type: 'text/calendar;charset=utf-8',
  });
}

/**
 * Detects if the device is iOS (including Chrome and other browsers on iOS)
 */
function isIOS(): boolean {
  const ua = navigator.userAgent;

  // Method 1: Check user agent for iOS devices
  const isIOSUserAgent = /iPhone|iPad|iPod/i.test(ua);

  // Method 2: Check for iPad on iOS 13+ (reports as Macintosh)
  const isIPadOS = /Macintosh/i.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;

  // Method 3: Check if it's a mobile device with Safari-like features
  const isMobileSafari = /Safari/i.test(ua) && /Mobile/i.test(ua) && !/Chrome|CriOS|FxiOS/i.test(ua);

  const result = isIOSUserAgent || isIPadOS || isMobileSafari;

  console.log('iOS Detection:', {
    userAgent: ua.substring(0, 100),
    isIOSUserAgent,
    isIPadOS,
    isMobileSafari,
    maxTouchPoints: navigator.maxTouchPoints,
    result
  });

  return result;
}

/**
 * Detects if the browser is Chrome (including on iOS)
 */
function isChrome(): boolean {
  return /Chrome/i.test(navigator.userAgent) && !/Edg|OPR|Safari/i.test(navigator.userAgent.replace(/Chrome/i, ''));
}

/**
 * Detects if the browser is Safari (including on iOS)
 */
function isSafari(): boolean {
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS/i.test(ua);
}

/**
 * Detects if the device is Android
 */
function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

/**
 * Detects if the device is mobile
 */
function isMobileDevice(): boolean {
  return isIOS() || isAndroid() || /webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Opens or downloads an ICS file
 * Platform-specific handling:
 * - iOS: Opens in Calendar app directly (uses data URL approach)
 * - Android: Uses Web Share API or downloads file (may need manual import)
 * - Desktop: Downloads the file
 */
function downloadICS(icsContent: string, filename: string = 'event.ics'): void {
  if (isIOS()) {
    console.log('downloadICS called for iOS, browser:', isChrome() ? 'Chrome' : isSafari() ? 'Safari' : 'Other');

    // iOS Safari: Use blob URL - Safari recognizes it and opens Calendar app
    // iOS Chrome: Use data URL - Chrome doesn't handle blob URLs for .ics properly
    if (isChrome()) {
      console.log('Using data URL for Chrome on iOS');
      // Data URL approach for Chrome on iOS
      const dataUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;

      // Try window.location - this sometimes works better than link.click() in Chrome
      window.location.href = dataUrl;

      // Also try creating a link as fallback
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          if (link.parentNode) {
            document.body.removeChild(link);
          }
        }, 100);
      }, 100);
    } else {
      console.log('Using blob URL for Safari on iOS');
      // Blob URL approach for Safari
      const blob = createICSBlob(icsContent);
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      // IMPORTANT: No download attribute - let iOS handle the file type
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      // Clean up after delay
      setTimeout(() => {
        try {
          if (link.parentNode) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors
        }
      }, 1000);
    }
  } else if (isAndroid()) {
    // Android: Chrome may download the file or open it depending on browser
    const blob = createICSBlob(icsContent);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // Clean up after a delay
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 1000);
  } else {
    // Desktop: download the file
    const blob = createICSBlob(icsContent);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Checks if Web Share API is available and supports files
 */
function isWebShareAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'share' in navigator &&
    'canShare' in navigator
  );
}

/**
 * Adds a calendar event to the device calendar
 *
 * On mobile devices with Web Share API support, it will use the native share
 * functionality. Otherwise, it downloads an .ics file that can be imported
 * into calendar applications.
 *
 * @param event - The calendar event to add
 * @param options - Additional options
 * @returns Promise that resolves when the event is added/shared
 */
/**
 * Creates a Google Calendar URL (alternative for Android)
 */
function createGoogleCalendarURL(event: CalendarEvent): string {
  const params = new URLSearchParams();
  params.set('action', 'TEMPLATE');
  params.set('text', event.title);

  if (event.description) {
    params.set('details', event.description);
  }

  if (event.location) {
    params.set('location', event.location);
  }

  // Format dates for Google Calendar (YYYYMMDDTHHmmssZ)
  const formatGoogleDate = (date: Date): string => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
  };

  params.set('dates', `${formatGoogleDate(event.startDate)}/${formatGoogleDate(event.endDate)}`);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export async function addToCalendar(
  event: CalendarEvent,
  options: {
    filename?: string;
    preferWebShare?: boolean;
    allowAppSelection?: boolean; // If true, shows share sheet to let user choose app (default: true)
  } = {}
): Promise<void> {
  // Ensure filename has .ics extension
  let finalFilename = options.filename || 'event.ics';
  if (!finalFilename.toLowerCase().endsWith('.ics')) {
    finalFilename += '.ics';
  }

  const { preferWebShare = true, allowAppSelection = true } = options;

  const icsContent = generateICS(event);
  const blob = createICSBlob(icsContent);

  // Create File with proper extension and MIME type
  const file = new File([blob], finalFilename, {
    type: 'text/calendar',
    lastModified: Date.now(),
  });

  console.log('addToCalendar called:', {
    platform: isIOS() ? 'iOS' : isAndroid() ? 'Android' : 'Desktop',
    browser: isSafari() ? 'Safari' : isChrome() ? 'Chrome' : 'Other',
    webShareAvailable: isWebShareAvailable(),
    filename: finalFilename,
    fileType: file.type,
    fileSize: file.size
  });

  // For iOS: DO NOT use Web Share API for calendar files
  // Web Share API shows file-sharing apps (WhatsApp, Messages, etc.), not calendar apps
  // Instead, open the .ics file directly - iOS recognizes it and opens Calendar app
  if (isIOS()) {
    downloadICS(icsContent, finalFilename);
    return;
  }

  // For Android: Try Web Share API first
  if (isAndroid() && preferWebShare && isWebShareAvailable()) {
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: event.title,
          text: event.description || `Add ${event.title} to your calendar`,
          files: [file],
        });
        return;
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.warn('Web Share API failed on Android, trying direct file open:', error);
    }
  }

  // Desktop or fallback: download the file
  downloadICS(icsContent, finalFilename);
}

/**
 * Creates a data URL for the ICS file (useful for direct links)
 */
export function getICSDataURL(event: CalendarEvent): string {
  const icsContent = generateICS(event);
  const blob = createICSBlob(icsContent);
  return URL.createObjectURL(blob);
}

// Re-export CalendarEvent for convenience
export type { CalendarEvent };

